import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Icons } from '../../components/common/Icons';
import { ROLE_DASHBOARD_PATHS, APP_NAME, APP_SUBTITLE } from '../../utils/constants';
import { isValidEmail } from '../../utils/helpers';
import { ChangePasswordModal } from './ChangePasswordModal';
import omLogo from '../../images/images.jpg';
import './Login.css';

interface LocationState {
  from?: { pathname: string };
}

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, user, userRole, error, clearError, isLoading, refreshPermissions } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({ email: '', password: '' });
  
  // Password change modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  const locationState = location.state as LocationState;
  const from = locationState?.from?.pathname || '/dashboard';

  // Redirect if already authenticated (and doesn't need password change)
  useEffect(() => {
    if (isAuthenticated && user && userRole) {
      // Check if user must change password
      if (user.must_change_password) {
        setShowChangePasswordModal(true);
        return;
      }
      const redirectPath = ROLE_DASHBOARD_PATHS[userRole];
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, userRole, navigate]);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [email, password]);

  const validateForm = (): boolean => {
    const errors = { email: '', password: '' };
    let isValid = true;

    if (!email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email';
      isValid = false;
    }

    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Store the password temporarily for password change modal
      setTempPassword(password);
      await login({ email, password });
      // Navigation will happen via useEffect when isAuthenticated changes
      // If must_change_password is true, modal will be shown instead
    } catch (err) {
      // Error is handled by auth context
    }
  };

  // Handle successful password change
  const handlePasswordChangeSuccess = async () => {
    // Refresh user data to clear must_change_password flag
    await refreshPermissions();
    setShowChangePasswordModal(false);
    // Navigation will happen via useEffect
    if (userRole) {
      navigate(ROLE_DASHBOARD_PATHS[userRole], { replace: true });
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img src={omLogo} alt="Logo" />
            </div>
            <div className="login-brand">
              <span className="brand-name">{APP_NAME}</span>
              <span className="brand-subtitle">{APP_SUBTITLE}</span>
            </div>
          </div>

          <div className="login-form-section">
            <h1 className="login-title">Sign in</h1>
            <p className="login-subtitle">Enter your credentials to access your account</p>

            <form className="login-form" onSubmit={handleSubmit}>
              {error && (
                <div className="login-error">
                  <Icons.AlertCircle />
                  <span>{error}</span>
                </div>
              )}

              <Input
                label="Email Address"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={formErrors.email}
                leftIcon={<Icons.Email />}
                autoComplete="email"
              />

              <div className="password-field">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={formErrors.password}
                  leftIcon={<Icons.Lock />}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                </button>
              </div>

              <Button
                type="submit"
                fullWidth
                size="lg"
                isLoading={isLoading}
              >
                Sign In
              </Button>
            </form>

            <div className="login-help">
              <Link to="/forgot-password" className="help-link">
                Having trouble? <span>Get help signing in</span>
              </Link>
            </div>
          </div>
        </div>

        <p className="login-footer">
          &copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </p>
      </div>

      {/* Change Password Modal - shown when must_change_password is true */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        tempPassword={tempPassword}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
}

export default Login;
